import React from "react";
import {
  Box,
  Container,
  SimpleGrid,
  Skeleton,
  SkeletonText,
  VStack,
  HStack,
  useBreakpointValue,
} from "@chakra-ui/react";

interface LoadingProps {
  type?: "cards" | "detail" | "music" | "general";
  count?: number;
}

const CardSkeletonItem: React.FC = () => (
  <Box
    position="relative"
    display="flex"
    flexDirection={{ base: "row", md: "column" }}
    borderRadius={{ base: "0px", md: "24px" }}
    bg={{ base: "transparent", md: "white" }}
    borderWidth={{ base: "0px", md: "1px" }}
    borderColor="gray.100"
    boxShadow={{ base: "none", md: "soft" }}
    p={{ base: "12px 0", md: "0px" }}
    height="100%"
    overflow="hidden"
  >
    {/* Poster Skeleton - Exactly matches Card.tsx poster container & aspect ratio */}
    <Box
      position="relative"
      width={{ base: "90px", md: "100%" }}
      minWidth={{ base: "90px", md: "100%" }}
      height={{ base: "112.5px", md: "auto" }}
      paddingBottom={{ base: "0", md: "125%" }} // 4:5 aspect ratio on desktop
      borderRadius={{ base: "8px", md: "0px" }}
      overflow="hidden"
      bg="gray.50"
    >
      <Skeleton
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        startColor="purple.50"
        endColor="purple.100"
      />
    </Box>

    {/* Content Area Skeleton - Exactly matches Card.tsx layout */}
    <VStack
      p={{ base: "2px 0 2px 12px", md: 4 }}
      spacing={{ base: 1, md: 3 }}
      align="stretch"
      flex="1"
      justify="space-between"
      height="100%"
    >
      <VStack align="start" spacing={{ base: 1.5, md: 2 }} width="100%">
        {/* State & Tag Badges Skeleton */}
        <HStack spacing={1.5}>
          <Skeleton
            height={{ base: "18px", md: "20px" }}
            width={{ base: "48px", md: "56px" }}
            borderRadius={{ base: "md", md: "full" }}
            startColor="purple.50"
            endColor="purple.100"
          />
          <Skeleton
            display={{ base: "none", md: "block" }}
            height="20px"
            width="44px"
            borderRadius="full"
            startColor="purple.50"
            endColor="purple.100"
          />
        </HStack>

        {/* Concert Title Skeleton */}
        <Skeleton
          height={{ base: "18px", md: "22px" }}
          width={{ base: "90%", md: "85%" }}
          borderRadius="6px"
          startColor="purple.50"
          endColor="purple.100"
          mt={{ base: 0.5, md: 1 }}
        />
        <Skeleton
          display={{ base: "block", md: "none" }}
          height="14px"
          width="65%"
          borderRadius="4px"
          startColor="purple.50"
          endColor="purple.100"
        />

        {/* Location & Date Metadata Skeleton */}
        <VStack align="start" spacing={1} width="100%" mt={{ base: 1, md: 1.5 }}>
          <Skeleton height="14px" width="70%" borderRadius="4px" startColor="purple.50" endColor="purple.100" />
          <Skeleton height="14px" width="50%" borderRadius="4px" startColor="purple.50" endColor="purple.100" />
        </VStack>
      </VStack>

      {/* Button Skeleton (Desktop Only) */}
      <Skeleton
        display={{ base: "none", md: "block" }}
        height="40px"
        borderRadius="14px"
        startColor="purple.50"
        endColor="purple.100"
        mt={2}
      />
    </VStack>
  </Box>
);

const DetailSkeletonItem: React.FC = () => (
  <Container maxW="container.md" py={6} px={{ base: 4, md: 6 }}>
    <VStack spacing={6} align="stretch">
      {/* Top Poster Skeleton */}
      <Skeleton
        height={{ base: "320px", md: "460px" }}
        borderRadius="28px"
        startColor="purple.50"
        endColor="purple.100"
      />

      {/* Badges & Title Skeleton */}
      <Box bg="white" p={6} borderRadius="24px" border="1px solid" borderColor="brand.border">
        <HStack spacing={2} mb={3}>
          <Skeleton height="24px" width="70px" borderRadius="full" startColor="purple.50" endColor="purple.100" />
          <Skeleton height="24px" width="50px" borderRadius="full" startColor="purple.50" endColor="purple.100" />
        </HStack>
        <Skeleton height="28px" width="90%" borderRadius="8px" startColor="purple.50" endColor="purple.100" mb={3} />
        <SkeletonText noOfLines={3} spacing={3} skeletonHeight="14px" startColor="purple.50" endColor="purple.100" />
      </Box>

      {/* Tabs / Info Sections Skeleton */}
      <Box bg="white" p={6} borderRadius="24px" border="1px solid" borderColor="brand.border">
        <HStack spacing={4} mb={5}>
          <Skeleton height="36px" width="100px" borderRadius="12px" startColor="purple.50" endColor="purple.100" />
          <Skeleton height="36px" width="100px" borderRadius="12px" startColor="purple.50" endColor="purple.100" />
          <Skeleton height="36px" width="100px" borderRadius="12px" startColor="purple.50" endColor="purple.100" />
        </HStack>
        <VStack spacing={3} align="stretch">
          <Skeleton height="50px" borderRadius="14px" startColor="purple.50" endColor="purple.100" />
          <Skeleton height="50px" borderRadius="14px" startColor="purple.50" endColor="purple.100" />
          <Skeleton height="50px" borderRadius="14px" startColor="purple.50" endColor="purple.100" />
        </VStack>
      </Box>
    </VStack>
  </Container>
);

const MusicSkeletonItem: React.FC = () => (
  <Container maxW="container.lg" py={6} px={{ base: 4, md: 6 }}>
    {/* Search bar Skeleton */}
    <Skeleton height="42px" maxW="420px" mx="auto" borderRadius="full" startColor="purple.50" endColor="purple.100" mb={6} />

    {/* Albums Grid Skeleton */}
    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={5}>
      {Array.from({ length: 6 }).map((_, i) => (
        <Box
          key={i}
          bg="white"
          p={4}
          borderRadius="22px"
          border="1px solid"
          borderColor="brand.border"
          boxShadow="0 4px 16px rgba(142, 41, 36, 0.04)"
        >
          <Skeleton height="180px" borderRadius="16px" startColor="purple.50" endColor="purple.100" mb={3.5} />
          <Skeleton height="20px" width="75%" borderRadius="6px" startColor="purple.50" endColor="purple.100" mb={2} />
          <Skeleton height="14px" width="45%" borderRadius="4px" startColor="purple.50" endColor="purple.100" />
        </Box>
      ))}
    </SimpleGrid>
  </Container>
);

const Loading: React.FC<LoadingProps> = ({ type = "general", count }) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const defaultCount = count ?? (isMobile ? 5 : 8);

  if (type === "detail") {
    return <DetailSkeletonItem />;
  }

  if (type === "music") {
    return <MusicSkeletonItem />;
  }

  return (
    <Box width="100%">
      <SimpleGrid columns={{ base: 1, md: 3, lg: 4 }} spacing={{ base: 4, md: 6 }}>
        {Array.from({ length: defaultCount }).map((_, idx) => (
          <CardSkeletonItem key={idx} />
        ))}
      </SimpleGrid>
    </Box>
  );
};

export default Loading;
